import { Link, createFileRoute } from '@tanstack/react-router';
import { isMarketingOnlyMode } from '@/lib/marketingOnly';

export const Route = createFileRoute('/contact')({
  component: ContactPage,
});

const CONTACT_EMAIL = 'support@pirouettehq.com';

function ContactPage() {
  return (
    <main
      id="main"
      className="min-h-dvh bg-[var(--color-surface-sunken)] px-6 py-10 text-[var(--color-ink)] sm:px-10 lg:px-16"
      data-testid="contact-page"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-10">
        <nav aria-label="Public navigation" className="flex items-center justify-between gap-6">
          <Link
            to="/"
            className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--text-lg)] uppercase"
          >
            Pirouette
          </Link>
          {!isMarketingOnlyMode && (
            <div className="flex items-center gap-5 text-[var(--text-sm)]">
              <Link to="/login" className="text-[var(--color-ink-muted)] hover:underline">
                Sign in
              </Link>
              <Link to="/signup" className="text-[var(--color-ink-muted)] hover:underline">
                Create account
              </Link>
            </div>
          )}
        </nav>

        <section className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <p className="text-[length:var(--text-base)] font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[color:var(--color-accent)] uppercase">
              Contact Pirouette
            </p>
            <h1 className="mt-4 max-w-3xl leading-[var(--leading-tight)] font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-display)]">
              Questions about running your studio with Pirouette?
            </h1>
            <p className="mt-6 max-w-2xl text-[length:var(--text-lg)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
              Reach out for product questions, studio fit reviews, or help with an existing account.
              Application access is currently paused, and we will route your note to the right
              Pirouette contact.
            </p>
          </div>

          <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-md)]">
            <h2 className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-2xl)]">
              Contact details
            </h2>
            <dl className="mt-6 space-y-6">
              <div>
                <dt className="text-[length:var(--text-sm)] text-[color:var(--color-ink-muted)]">
                  Email
                </dt>
                <dd className="mt-2">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-[length:var(--text-xl)] font-semibold text-[color:var(--color-accent)] hover:underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </dd>
              </div>
            </dl>

            <div className="mt-8">
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Pirouette%20studio%20fit%20review`}
                className="inline-flex min-h-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-5 py-3 text-center font-medium text-white shadow-[var(--shadow-sm)] hover:opacity-95"
              >
                Email Pirouette
              </a>
            </div>

            <p className="mt-5 text-[length:var(--text-sm)] text-[color:var(--color-ink-muted)]">
              SMS alerts are optional.{' '}
              <Link to="/sms-consent" className="text-[color:var(--color-accent)] underline">
                View SMS consent terms
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          {[
            [
              'Studio fit reviews',
              'See whether Pirouette matches your season, team, and studio size.',
            ],
            [
              'Onboarding questions',
              'Ask about roster imports, setup support, and launch planning.',
            ],
            [
              'Account support',
              'Existing accounts are paused. Contact us for access, export, or support questions.',
            ],
          ].map(([title, body]) => (
            <article
              key={title}
              className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
            >
              <h2 className="font-semibold text-[var(--text-lg)]">{title}</h2>
              <p className="mt-2 text-[length:var(--text-sm)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
                {body}
              </p>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}
